import { Copy } from "lucide-react";
import { toast } from "sonner";
import { fmtDate, inr } from "@/lib/api";
import { useMember } from "@/pages/Member";
import { ReferralQr } from "@/components/member/ReferralQr";
import { SetPassword } from "@/components/member/SetPassword";

export const Stat = ({ label, value, testid }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testid}>
    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
    <p className="font-mono-num mt-2 text-2xl font-bold">{value}</p>
  </div>
);

export const Card = ({ title, children, testid }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testid}>
    <h3 className="mb-3 font-display font-bold">{title}</h3>
    {children}
  </div>
);

export const MemberOverview = () => {
  const [d] = useMember("/mlm/me/dashboard");
  if (!d) return <p className="text-slate-500">Loading…</p>;
  const p = d.profile;
  const link = `${window.location.origin}/distributor/register?ref=${p.distributor_id}`;
  const copy = () => navigator.clipboard.writeText(link).then(() => toast.success("Referral link copied"));
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="Distributor ID" value={p.distributor_id} testid="member-distributor-id" />
        <Stat label="Personal product sales" value={inr(d.personal_sales.amount)} testid="member-personal-sales" />
        <Stat label="Team product sales" value={inr(d.team_sales.amount)} testid="member-team-sales" />
        <Stat label="Wallet balance" value={inr(d.wallet_balance)} testid="member-wallet-balance" />
      </div>
      <Card title="Your referral link" testid="member-referral-card">
        <div className="flex flex-wrap items-center gap-2">
          <code className="min-w-0 flex-1 truncate rounded-lg bg-slate-100 px-3 py-2 text-sm" data-testid="member-referral-link">{link}</code>
          <button onClick={copy} className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white" data-testid="member-copy-referral"><Copy className="h-4 w-4" /> Copy</button>
        </div>
        <p className="mt-2 text-xs text-slate-500">Product shop link with your ID: {window.location.origin}/products?ref={p.distributor_id}</p>
        <ReferralQr link={link} id={p.distributor_id} name={p.full_name} />
      </Card>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Personal profile" testid="member-profile">
          <dl className="grid gap-2 text-sm sm:grid-cols-2">
            {[["Name", p.full_name], ["Mobile", p.mobile], ["Email", p.email], ["District", p.district], ["State", p.state], ["PIN", p.pincode], ["Address", p.address], ["Approved on", fmtDate(p.approved_at)]].map(([k, v]) => <div key={k}><dt className="text-xs text-slate-500">{k}</dt><dd className="break-words font-medium">{v || "—"}</dd></div>)}
          </dl>
        </Card>
        <Card title="Sponsor details" testid="member-sponsor">
          {d.sponsor ? <p className="text-sm"><span className="font-semibold">{d.sponsor.full_name}</span> · {d.sponsor.distributor_id} · {d.sponsor.district}</p> : <p className="text-sm text-slate-500">No sponsor (joined directly with the company).</p>}
          <div className="mt-4 grid grid-cols-3 gap-3 text-center text-sm">
            <div className="rounded-xl bg-slate-50 p-3"><p className="font-mono-num text-xl font-bold" data-testid="member-team-total">{d.team.total}</p><p className="text-xs text-slate-500">Team</p></div>
            <div className="rounded-xl bg-emerald-50 p-3"><p className="font-mono-num text-xl font-bold">{d.team.active}</p><p className="text-xs text-slate-500">Active</p></div>
            <div className="rounded-xl bg-slate-50 p-3"><p className="font-mono-num text-xl font-bold">{d.team.inactive}</p><p className="text-xs text-slate-500">Inactive</p></div>
          </div>
        </Card>
      </div>
      <Card title={`Direct referrals (${d.directs.length})`} testid="member-directs">
        {d.directs.length ? (
          <div className="divide-y divide-slate-100 text-sm">{d.directs.map((r, i) => <div key={i} className="flex flex-wrap justify-between gap-2 py-2"><span className="font-semibold">{r.full_name} <span className="font-normal text-slate-500">{r.distributor_id || "ID on approval"}</span></span><span className="text-slate-500">{r.district} · <span className="capitalize">{r.status}</span> · joined {fmtDate(r.registered_at)}</span></div>)}</div>
        ) : <p className="text-sm text-slate-500">No direct referrals yet. Share your referral link to invite members.</p>}
      </Card>
      <SetPassword />
    </div>
  );
};
